import {
  BaseApiClient,
  GetWorkflowRequestBody,
  ListWorkflowRequestBody,
  MultipleWorkflowRunResult,
  ReRunMultipleWorkflowRequestBody,
  ReRunWorkflowRequestBody,
  WorkflowRunResult,
  WorkflowRunLogsResponse,
} from '@internal/backstage-plugin-cloudbuild-common';
import { WorkflowApi } from './WorkflowApi';
import {
  ActionsListWorkflowRunsForRepoResponseData,
  ActionsGetWorkflowResponseData,
} from './types';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export class WorkflowClient extends BaseApiClient implements WorkflowApi {
  private readonly cache = new Map<
    string,
    { timestamp: number; response: any }
  >();
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    super(options.discoveryApi, options.fetchApi);
  }

  private async fetchWithCache<T>(
    key: string,
    fetcher: () => Promise<T>,
  ): Promise<T> {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.response as T;
    }
    const response = await fetcher();
    this.cache.set(key, { timestamp: Date.now(), response });
    return response;
  }

  invalidateCache(): void {
    this.cache.clear();
  }

  async reRunMultipleWorkflows(
    options: ReRunMultipleWorkflowRequestBody,
  ): Promise<MultipleWorkflowRunResult> {
    const result = await this.post<MultipleWorkflowRunResult>(
      'workflow/rerun-multiple',
      options,
    );
    this.invalidateCache();
    return result;
  }

  async reRunWorkflow(
    options: ReRunWorkflowRequestBody,
  ): Promise<WorkflowRunResult> {
    const result = await this.post<WorkflowRunResult>('workflow/rerun', options);
    this.invalidateCache();
    return result;
  }

  async listWorkflowRuns(
    options: ListWorkflowRequestBody,
  ): Promise<ActionsListWorkflowRunsForRepoResponseData> {
    return this.fetchWithCache(
      `listWorkflowRuns-${JSON.stringify(options)}`,
      () =>
        this.post<ActionsListWorkflowRunsForRepoResponseData>(
          'workflow/list',
          options,
        ),
    );
  }

  async getWorkflow(
    options: GetWorkflowRequestBody,
  ): Promise<ActionsGetWorkflowResponseData> {
    return this.fetchWithCache(`getWorkflow-${JSON.stringify(options)}`, () =>
      this.post<ActionsGetWorkflowResponseData>('workflow/get', options),
    );
  }

  async getWorkflowRun(
    options: GetWorkflowRequestBody,
  ): Promise<ActionsGetWorkflowResponseData> {
    return this.fetchWithCache(
      `getWorkflowRun-${JSON.stringify(options)}`,
      async () => {
        const returnValue = await this.post<ActionsGetWorkflowResponseData>(
          'workflow/run',
          options,
        );
        return returnValue;
      },
    );
  }

  async getWorkflowLogs(
    options: GetWorkflowRequestBody,
  ): Promise<WorkflowRunLogsResponse | null> {
    return this.fetchWithCache(
      `getWorkflowLogs-${JSON.stringify(options)}`,
      () => this.post<WorkflowRunLogsResponse | null>('workflow/logs', options),
    );
  }
}
